import { useRef, useState } from "react";
import { ImagePicker } from "./fields";
import { uploadImage, resolveAsset } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, ChevronLeft, ChevronRight, RefreshCw, ImageIcon } from "lucide-react";

const MIN_SLOTS = 6;

export const FotosTab = ({ draft, setDraft }) => {
  const addRef = useRef();
  const replaceRef = useRef();
  const [busy, setBusy] = useState(false);
  const [replaceIdx, setReplaceIdx] = useState(null);
  const gallery = draft.gallery || [];

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= gallery.length) return;
    const g = [...gallery];
    [g[i], g[j]] = [g[j], g[i]];
    setDraft({ ...draft, gallery: g });
  };

  const onAdd = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const url = await uploadImage(f);
      setDraft({ ...draft, gallery: [...gallery, url] });
      toast.success("Fotografía agregada. Recuerda Guardar cambios.");
    } catch {
      toast.error("Error al subir la imagen");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const onReplace = async (e) => {
    const f = e.target.files?.[0];
    if (!f || replaceIdx === null) return;
    setBusy(true);
    try {
      const url = await uploadImage(f);
      const g = [...gallery];
      g[replaceIdx] = url;
      setDraft({ ...draft, gallery: g });
      toast.success("Fotografía reemplazada");
    } catch {
      toast.error("Error al subir la imagen");
    } finally {
      setBusy(false);
      setReplaceIdx(null);
      e.target.value = "";
    }
  };

  const emptySlots = Math.max(0, MIN_SLOTS - gallery.length);

  return (
    <div className="space-y-7">
      <ImagePicker label="Foto de fondo del sitio" testId="foto-fondo-upload" value={draft.photos?.fondo}
        onChange={(v) => setDraft({ ...draft, photos: { ...draft.photos, fondo: v } })} />
      <ImagePicker label="Foto de la sección Mi Historia" testId="foto-historia-upload" allowRemove
        value={draft.photos?.historia}
        onChange={(v) => setDraft({ ...draft, photos: { ...draft.photos, historia: v } })} />
      <div>
        <span className="text-xs font-medium uppercase tracking-wide text-neutral-500">
          Galería (en la página pública solo se muestran las fotos subidas)
        </span>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {gallery.map((url, i) => (
            <div key={i} className="rounded-lg border border-neutral-200 overflow-hidden">
              <img src={resolveAsset(url)} alt={`Galería ${i + 1}`} className="h-28 w-full object-cover" />
              <div className="flex items-center justify-between px-1.5 py-1 bg-neutral-50">
                <button type="button" data-testid={`gallery-left-${i}`} onClick={() => move(i, -1)} disabled={i === 0}
                  className="p-1 text-neutral-600 disabled:opacity-30" aria-label="Mover a la izquierda">
                  <ChevronLeft size={15} />
                </button>
                <button type="button" data-testid={`gallery-replace-${i}`}
                  onClick={() => { setReplaceIdx(i); replaceRef.current?.click(); }}
                  className="p-1 text-[#0057FF]" aria-label="Reemplazar">
                  <RefreshCw size={14} />
                </button>
                <button type="button" data-testid={`gallery-remove-${i}`}
                  onClick={() => setDraft({ ...draft, gallery: gallery.filter((_, j) => j !== i) })}
                  className="p-1 text-red-600" aria-label="Eliminar">
                  <Trash2 size={14} />
                </button>
                <button type="button" data-testid={`gallery-right-${i}`} onClick={() => move(i, 1)}
                  disabled={i === gallery.length - 1}
                  className="p-1 text-neutral-600 disabled:opacity-30" aria-label="Mover a la derecha">
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          ))}
          {Array.from({ length: emptySlots }).map((_, i) => (
            <button key={`empty-${i}`} type="button" data-testid={`gallery-empty-slot-${i}`} disabled={busy}
              onClick={() => addRef.current?.click()}
              className="h-[7.5rem] rounded-lg border-2 border-dashed border-neutral-300 text-neutral-400 flex flex-col items-center justify-center gap-1 text-xs hover:border-[#0057FF]/50 hover:text-[#0057FF] disabled:opacity-60">
              <ImageIcon size={18} />
              Espacio {gallery.length + i + 1}
            </button>
          ))}
          <button type="button" data-testid="gallery-add-button" disabled={busy}
            onClick={() => addRef.current?.click()}
            className="h-[7.5rem] rounded-lg border-2 border-dashed border-[#0057FF]/40 text-[#0057FF] flex flex-col items-center justify-center gap-1 text-xs disabled:opacity-60">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
            Agregar foto
          </button>
        </div>
        <input ref={addRef} type="file" accept="image/*" className="hidden" onChange={onAdd} />
        <input ref={replaceRef} type="file" accept="image/*" className="hidden" onChange={onReplace} />
      </div>
    </div>
  );
};
